import {
    SpeechRecognitionEngineState,
    SpeechRecognitionProvider,
    SpeechRecognitionStartOptions,
} from './speech-recognition-provider'

interface WebkitSpeechRecognition {
    continuous: boolean
    interimResults: boolean
    lang: string
    onresult: ((event: SpeechRecognitionEventLike) => void) | null
    onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null
    onend: (() => void) | null
    onstart: (() => void) | null
    start(): void
    stop(): void
    abort(): void
}

interface SpeechRecognitionEventLike {
    results: { length: number; [index: number]: { isFinal: boolean; [index: number]: { transcript: string } } }
}

interface SpeechRecognitionErrorEventLike {
    error: string
}

const FATAL_ERRORS = new Set(['not-allowed', 'service-not-allowed', 'audio-capture', 'language-not-supported'])
const RESTART_DELAY_MS = 250
const MAX_RESTARTS_IN_WINDOW = 5
const RESTART_WINDOW_MS = 5000
const ABORT_WATCHDOG_MS = 800
const FINAL_WATCHDOG_MS = 1500

function getSpeechRecognitionCtor(): (new () => WebkitSpeechRecognition) | null {
    const w = window as Window & {
        SpeechRecognition?: new () => WebkitSpeechRecognition
        webkitSpeechRecognition?: new () => WebkitSpeechRecognition
    }
    return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

function isAppleVendor(): boolean {
    return typeof navigator !== 'undefined' && navigator.vendor.includes('Apple')
}

export class WebSpeechProvider implements SpeechRecognitionProvider {
    readonly name = 'Web Speech API'

    get isSupported(): boolean {
        return getSpeechRecognitionCtor() !== null
    }

    private state: SpeechRecognitionEngineState = 'idle'
    private recognition: WebkitSpeechRecognition | null = null
    private intentionallyStopped = false
    private pendingStop = false
    private committedText = ''
    private lang = 'en-US'

    private onResult: ((finalText: string, interimText: string) => void) | null = null
    private onError: ((error: string) => void) | null = null
    private onStateChange: ((state: SpeechRecognitionEngineState) => void) | null = null

    private restartTimer: ReturnType<typeof setTimeout> | null = null
    private abortWatchdog: ReturnType<typeof setTimeout> | null = null
    private finalWatchdog: ReturnType<typeof setTimeout> | null = null
    private restartTimestamps: number[] = []

    start(
        lang: string,
        onResult: (finalText: string, interimText: string) => void,
        onError: (error: string) => void,
        options?: SpeechRecognitionStartOptions,
    ): void {
        const Ctor = getSpeechRecognitionCtor()
        if (!Ctor) {
            onError('Web Speech API is not supported in this browser. Try Chrome or Edge.')
            return
        }

        this.clearTimers()
        this.intentionallyStopped = false
        this.pendingStop = false
        this.committedText = ''
        this.lang = lang
        this.onResult = onResult
        this.onError = onError
        this.onStateChange = options?.onStateChange ?? null
        this.restartTimestamps = []

        this.discardRecognition()

        this.recognition = new Ctor()
        this.attachHandlers()
        this.beginListening()
    }

    stop(): void {
        this.intentionallyStopped = true
        this.clearRestartTimer()

        if (!this.recognition) {
            this.setState('idle')
            return
        }

        if (this.state === 'starting') {
            this.pendingStop = true
            this.setState('stopping')
            return
        }

        this.detachHandlers()
        this.forceStopRecognition()
    }

    private beginListening(): void {
        const recognition = this.recognition
        if (!recognition || this.intentionallyStopped) {
            return
        }

        recognition.continuous = false
        recognition.interimResults = true
        recognition.lang = this.lang

        this.setState('starting')

        try {
            recognition.start()
        } catch {
            this.discardRecognition()
            this.onError?.('Unable to start speech recognition. Try again.')
            this.setState('idle')
        }
    }

    private attachHandlers(): void {
        const recognition = this.recognition
        if (!recognition) {
            return
        }

        recognition.onstart = () => this.handleStart()
        recognition.onresult = (event: SpeechRecognitionEventLike) => this.handleResult(event)
        recognition.onerror = (event: SpeechRecognitionErrorEventLike) => this.handleError(event)
        recognition.onend = () => this.handleEnd()
    }

    private detachHandlers(): void {
        const recognition = this.recognition
        if (!recognition) {
            return
        }

        recognition.onstart = null
        recognition.onresult = null
        recognition.onerror = null
        recognition.onend = null
    }

    private handleStart(): void {
        this.clearAbortWatchdogs()

        if (this.pendingStop || this.intentionallyStopped) {
            this.detachHandlers()
            this.forceStopRecognition()
            return
        }

        this.setState('listening')
    }

    private handleResult(event: SpeechRecognitionEventLike): void {
        if (this.intentionallyStopped || !this.onResult) {
            return
        }

        const lastResult = event.results[event.results.length - 1]
        const transcript = lastResult[0].transcript

        if (lastResult.isFinal) {
            this.committedText += (this.committedText ? ' ' : '') + transcript.trim()
            this.onResult(this.committedText, '')
        } else {
            this.onResult(this.committedText, transcript)
        }
    }

    private handleError(event: SpeechRecognitionErrorEventLike): void {
        if (event.error === 'no-speech' || event.error === 'aborted') {
            return
        }

        if (FATAL_ERRORS.has(event.error)) {
            this.intentionallyStopped = true
            this.clearRestartTimer()
        }

        this.onError?.(`Speech recognition error: ${event.error}`)
    }

    private handleEnd(): void {
        this.clearAbortWatchdogs()

        if (this.intentionallyStopped) {
            this.setState('idle')
            return
        }

        if (!this.canRestart()) {
            this.intentionallyStopped = true
            this.onError?.('Speech recognition stopped unexpectedly.')
            this.setState('idle')
            return
        }

        this.scheduleRestart()
    }

    private canRestart(): boolean {
        return !this.intentionallyStopped && this.onResult !== null && getSpeechRecognitionCtor() !== null
    }

    private scheduleRestart(): void {
        this.clearRestartTimer()

        const now = Date.now()
        this.restartTimestamps = this.restartTimestamps.filter(t => now - t < RESTART_WINDOW_MS)
        this.restartTimestamps.push(now)

        if (this.restartTimestamps.length > MAX_RESTARTS_IN_WINDOW) {
            this.intentionallyStopped = true
            this.onError?.('Speech recognition stopped after too many restarts.')
            this.setState('idle')
            return
        }

        this.restartTimer = setTimeout(() => {
            this.restartTimer = null
            if (this.intentionallyStopped) {
                return
            }

            this.attachHandlers()
            this.beginListening()
        }, RESTART_DELAY_MS)
    }

    private forceStopRecognition(): void {
        this.setState('stopping')

        const recognition = this.recognition
        if (!recognition) {
            this.setState('idle')
            return
        }

        this.detachHandlers()

        if (isAppleVendor()) {
            try {
                recognition.start()
            } catch {
                // Already started — stop() below should still apply.
            }
        }

        try {
            recognition.stop()
        } catch {
            // Ignore — watchdog will abort if stop is ignored.
        }

        this.abortWatchdog = setTimeout(() => {
            this.abortWatchdog = null
            try {
                recognition.abort()
            } catch {
                // Ignore — final watchdog will discard the session.
            }

            this.finalWatchdog = setTimeout(() => {
                this.finalWatchdog = null
                this.discardRecognition()
                this.setState('idle')
            }, FINAL_WATCHDOG_MS)
        }, ABORT_WATCHDOG_MS)
    }

    private discardRecognition(): void {
        const recognition = this.recognition
        if (recognition) {
            this.detachHandlers()
            try {
                recognition.abort()
            } catch {
                // Ignore cleanup failures.
            }
        }
        this.recognition = null
    }

    private setState(state: SpeechRecognitionEngineState): void {
        this.state = state
        this.onStateChange?.(state)

        if (state === 'idle') {
            this.pendingStop = false
            this.onResult = null
            this.onError = null
            this.onStateChange = null
        }
    }

    private clearRestartTimer(): void {
        if (this.restartTimer !== null) {
            clearTimeout(this.restartTimer)
            this.restartTimer = null
        }
    }

    private clearAbortWatchdogs(): void {
        if (this.abortWatchdog !== null) {
            clearTimeout(this.abortWatchdog)
            this.abortWatchdog = null
        }
        if (this.finalWatchdog !== null) {
            clearTimeout(this.finalWatchdog)
            this.finalWatchdog = null
        }
    }

    private clearTimers(): void {
        this.clearRestartTimer()
        this.clearAbortWatchdogs()
    }
}
